import React, { useState } from 'react';
import {
  PaginationControls,
  PaginationDropdown,
  PaginationDropdownItem,
  PaginationDropdownLink,
  PaginationDropdownList,
  PaginationDropdownTrigger,
  TablePagination as EncoreTablePagination,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';

export const TESTID_SELECT_ROWS_PER_PAGE =
  'table-pagination-rows-per-page-dropdown';
export const TESTID_SELECT_ITEM_ROWS_PER_PAGE =
  'table-pagination-rows-per-page-dropdown-item';
export interface TablePaginationProps {
  pagination: {
    totalResultRows: number;
    page: number;
    // If true, the end result number for the current page will be the smaller value of
    // either a. current page * current limit or b. the *final* total results
    // otherwise the "end" result number will be calculated based on the pages *current* known total results
    // this is useful for APIs that cannot send the *final* total results count ahead of time
    finalTotalResultIsKnown?: boolean;
    isLastPage?: boolean;
    onDecrement: () => void;
    onIncrement: () => void;
  };
  limit: number;
  rowsPerPage?: {
    items: number[];
    onValueChange: (value: number) => void;
    customText?: string;
  };
}

export const TablePagination = ({
  pagination,
  limit,
  rowsPerPage,
}: TablePaginationProps) => {
  const t = useT();
  const [showSelectRowsPerPage, setShowSelectRowsPerPage] = useState(false);
  const start = (pagination.page - 1) * limit + 1;
  const end = pagination?.finalTotalResultIsKnown
    ? Math.min(pagination.page * limit, pagination.totalResultRows)
    : pagination.page * limit - (limit - pagination.totalResultRows);
  const handleRowsPerPageChange = (item: number) => {
    setShowSelectRowsPerPage(false);
    if (rowsPerPage?.onValueChange) {
      rowsPerPage.onValueChange(item);
    }
  };

  const of = t('atlas.table_pagination.of', 'of', 'Label for the word "of"');

  return (
    <EncoreTablePagination>
      {rowsPerPage && (
        <>
          <Type>
            {rowsPerPage.customText
              ? rowsPerPage.customText
              : t(
                  'atlas.table_pagination.rows_per_page',
                  'Rows per page:',
                  'Label for dropdown to select how many rows per page to show',
                )}
          </Type>
          <PaginationDropdownTrigger
            overlay={
              showSelectRowsPerPage && (
                <PaginationDropdownList>
                  {rowsPerPage.items.map((item, index) => (
                    <PaginationDropdownItem key={index}>
                      <PaginationDropdownLink
                        data-testid={TESTID_SELECT_ITEM_ROWS_PER_PAGE}
                        onClick={() => handleRowsPerPageChange(item)}
                      >
                        {item}
                      </PaginationDropdownLink>
                    </PaginationDropdownItem>
                  ))}
                </PaginationDropdownList>
              )
            }
            data-testid={TESTID_SELECT_ROWS_PER_PAGE}
            onShow={() => setShowSelectRowsPerPage(true)}
            onHide={() => setShowSelectRowsPerPage(false)}
          >
            <PaginationDropdown>{limit}</PaginationDropdown>
          </PaginationDropdownTrigger>
        </>
      )}

      <PaginationControls
        onDecrement={start > 1 ? () => pagination.onDecrement() : undefined}
        onIncrement={
          limit > pagination.totalResultRows || pagination?.isLastPage
            ? undefined
            : () => pagination.onIncrement()
        }
      >
        {start}-{end}
        {pagination?.finalTotalResultIsKnown
          ? ` ${of} ${pagination?.totalResultRows}`
          : ''}
      </PaginationControls>
    </EncoreTablePagination>
  );
};
